import { IAccount } from "lobbie";
import { BASE_API_URL, BASE_SECURE_ROUTE } from "src/constants/api";
import { includesSuperAdminRoleGroup } from "src/utils/roles";
import type { UserReducerState } from "./reducers/userReducer";

/*
This definition mirrors the store as these utility methods create a circular definition problem for the store.
This file is a maintenance concern until we can stop using stateful hooks as global utility methods.
*/
export type StateFragment = { user: UserReducerState; account: { account?: Partial<IAccount> } };

export const buildAdminResourcePathForCurrentUser = (
    state: StateFragment,
    desiredResource: string,
    additionalQueryParams: Record<string, string> = {},
) => {
    const isSuperAdmin = includesSuperAdminRoleGroup(state.user.user?.roleGroupIds);
    if (!isSuperAdmin) return null;

    const queryParams = new URLSearchParams(additionalQueryParams);
    const accountPublicUuid = state.account?.account?.publicUuid;
    if (!accountPublicUuid) throw new Error("refusing to build path without accountPublicUuid");
    queryParams.append("accountPublicUuid", accountPublicUuid);
    const resource = desiredResource.startsWith("/")
        ? desiredResource.substring(1)
        : desiredResource;
    // Avoid adding additional "admin/" prefix if it's already there
    const queryString = queryParams.size ? `?${queryParams.toString()}` : ``;
    const resourcePath = resource.startsWith("admin/") ? resource : `admin/${resource}`;
    return `${BASE_API_URL}/${BASE_SECURE_ROUTE}/${resourcePath}${queryString}`;
};

export const buildStaffResourcePathForCurrentUser = (
    desiredResource: string,
    additionalQueryParams: Record<string, string> = {},
) => {
    const queryParams = new URLSearchParams(additionalQueryParams);
    // Check if resource already starts with "staff/"
    const resource = desiredResource.startsWith("/")
        ? desiredResource.substring(1)
        : desiredResource;
    // Avoid adding additional "staff/" prefix if it's already there
    const resourcePath = resource.startsWith("staff/") ? resource : `staff/${resource}`;
    const queryString = queryParams.size ? `?${queryParams.toString()}` : ``;
    return `${BASE_API_URL}/${BASE_SECURE_ROUTE}/${resourcePath}${queryString}`;
};

export const addAccountPublicUuidToQueryParamsIfSuperAdmin = (
    state: StateFragment,
    endpoint: string,
) => {
    const isSuperAdmin = includesSuperAdminRoleGroup(state.user.user?.roleGroupIds);
    if (!isSuperAdmin) return endpoint;
    const accountPublicUuid = state.account?.account?.publicUuid;
    if (!accountPublicUuid) {
        // TODO: Find a different way to deal with invalid state rather than throwing/handling here.
        // handleError(new Error("UNEXPECTED STATE: super admin doesn't have an accountPublicUuid"));
        return endpoint;
    }
    const queryParams = new URLSearchParams(endpoint.split("?")[1]);
    queryParams.append("accountPublicUuid", accountPublicUuid);
    const queryString = queryParams.size ? `?${queryParams.toString()}` : ``;
    return `${endpoint.split("?")[0]}${queryString}`;
};

export const buildResourcePathForCurrentUser = (
    state: StateFragment,
    desiredResource: string,
    additionalQueryParams: Record<string, string> = {},
) => {
    const isSuperAdmin = includesSuperAdminRoleGroup(state.user.user?.roleGroupIds);
    return isSuperAdmin
        ? buildAdminResourcePathForCurrentUser(state, desiredResource, additionalQueryParams)
        : buildStaffResourcePathForCurrentUser(desiredResource, additionalQueryParams);
};
